import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useAuction } from '../../context/CleanAuctionContext';
import { useBalance } from '../../context/BalanceContext';

// Define the symbols with their weights and payouts
const gameSymbols = [
  // Regular symbols with their probabilities and payouts
  { symbol: 'Cherry', weight: 200, payout: 1.0, type: 'Common', probability: 5 },      // 1 in 5
  { symbol: 'Lemon', weight: 83.33, payout: 2.0, type: 'Common', probability: 12 },    // 1 in 12
  { symbol: 'Apple', weight: 40, payout: 3.0, type: 'Common', probability: 25 },       // 1 in 25
  { symbol: 'Blueberry', weight: 28.57, payout: 4.0, type: 'Common', probability: 35 }, // 1 in 35
  { symbol: 'Banana', weight: 20, payout: 5.0, type: 'Uncommon', probability: 50 },    // 1 in 50
  { symbol: 'Watermelon', weight: 10, payout: 10.0, type: 'Uncommon', probability: 100 }, // 1 in 100
  { symbol: 'Orange', weight: 5, payout: 15.0, type: 'Rare', probability: 200 },       // 1 in 200
  { symbol: 'Grape', weight: 2.5, payout: 25.0, type: 'Rare', probability: 400 },      // 1 in 400
  { symbol: 'GreenApple', weight: 1.25, payout: 40.0, type: 'Epic', probability: 800 }, // 1 in 800
  { symbol: 'Aubergine', weight: 0.67, payout: 75.0, type: 'Epic', probability: 1500 }, // 1 in 1500
  { symbol: 'Crown', weight: 0.53, payout: 125.0, type: 'Epic', probability: 1900 },   // 1 in 1900
  { symbol: 'Mushroom', weight: 0.26, payout: 250.0, type: 'Epic', probability: 3800 }, // 1 in 3800
  { symbol: 'Star', weight: 0.13, payout: 500.0, type: 'Legendary', probability: 7600 }, // 1 in 7600
  { symbol: 'Bell', weight: 0.07, payout: 1000.0, type: 'Legendary', probability: 14250 }, // 1 in 14250
  { symbol: 'Clove', weight: 0.035, payout: 5000.0, type: 'Mythic', probability: 28500 }, // 1 in 28500
  { symbol: 'Peppers', weight: 0.021, payout: 10000.0, type: 'Mythic', probability: 47500 }, // 1 in 47500
  
  // Special symbols
  { symbol: 'Ticket', weight: 500, payout: 0, type: 'Bonus', probability: 2 },     // 1 in 2
  { symbol: 'Chest', weight: 1.05, payout: 0, type: 'Bonus', probability: 950 }    // 1 in 950 (Bonus Game)
];

// Define the multipliers based on number of matches
const matchMultipliers = {
  3: 1.0,   // 3 matches = base payout
  4: 1.1,   // 4 matches = base + 0.1
  5: 1.2,   // 5 matches = base + 0.2
  6: 1.3,   // 6 matches = base + 0.3
  7: 1.4,   // 7 matches = base + 0.4
  8: 1.5,   // 8 matches = base + 0.5
  9: 1.6,   // 9 matches = base + 0.6
  10: 1.7,  // 10 matches = base + 0.7
  11: 1.8,  // 11 matches = base + 0.8
  12: 1.9   // 12 matches = base + 0.9
};

// Define the pool allocation percentages
const poolAllocations = {
  prizePool: 0.75,  // 75% of total bet goes to prize pool
  vipPool: 0.10,    // 10% of total bet goes to VIP pool
  lotteryPool: 0.02 // 2% of total bet goes to lottery pool
};

// Define the bonus features
const bonusFeatures = {
  chestGame: {
    minMultiplier: 5,
    maxMultiplier: 100,
    probability: 1/950  // 1 in 950 chance
  },
  lotteryGame: {
    weeklyPrize: 1000, // 1000 JINU prize
    drawDay: 5,        // Friday
    drawHour: 20,      // 8 PM
    drawMinute: 0
  }
};

const GameContext = createContext();

export const useGame = () => {
    const context = useContext(GameContext);
    if (!context) throw new Error('useGame must be used within a GameProvider');
    return context;
};

export function GameProvider({ children }) {
    const auction = useAuction();
    const { addToRewardsPool } = auction || {};
    const { balance, addToBalance, subtractFromBalance } = useBalance();

    // Initial game state
    const [gameState, setGameState] = useState({
        isPlaying: false,
        isRevealing: false,
        cards: [],
        revealedCards: Array(12).fill(false),
        betAmount: 1,
        winAmount: 0,
        matches: [],
        bonusFeatures: {
            activeBonus: null,
            bonusData: null,
            wheelBonus: false,
            wheelMultiplier: 1,
            chestBonus: false,
            chestMultiplier: 1,
            lotteryBonus: false,
            lotteryResult: null,
            pendingFreePlay: false
        },
        autoPlay: false,
        autoPlaySpeed: 1000, // Default speed
        autoPlayCount: 10, // Default count
        autoPlayRemaining: 0, // Default remaining
        ticketsCollected: 0,
        wheelTickets: 0,
        ticketHistory: [],
        sessionStats: {
            totalGames: 0,
            totalWins: 0,
            totalWagered: 0,
            biggestWin: 0,
            netProfit: 0
        },
        pools: {
            prizePool: 0,
            vipPool: 0,
            lotteryPool: 0,
            totalBets: 0
        }
    });

    // Use a ref to track the latest game state for auto play
    const gameStateRef = useRef(gameState);
    
    // Update the ref whenever game state changes
    useEffect(() => {
        gameStateRef.current = gameState;
    }, [gameState]);

    // Function to expose gameSymbols to avoid circular dependencies
    const getGameSymbols = useCallback(() => {
        return gameSymbols;
    }, []);

    // Function to expose matchMultipliers to avoid circular dependencies
    const getMatchMultipliers = useCallback(() => {
        return matchMultipliers;
    }, []);

    // Generate a random symbol based on weight distribution
    const getRandomSymbol = useCallback(() => {
        const totalWeight = gameSymbols.reduce((sum, s) => sum + s.weight, 0);
        let random = Math.random() * totalWeight;
        
        // Debug logging to help diagnose the issue
        console.log("Total weight:", totalWeight);
        console.log("Random value:", random);
        
        for (let s of gameSymbols) {
            // Debug logging
            console.log(`Symbol: ${s.symbol}, Weight: ${s.weight}, Remaining random: ${random}`);
            
            if (random < s.weight) {
                console.log(`Selected symbol: ${s.symbol}`);
                return s.symbol;
            }
            random -= s.weight;
        }
        
        console.log("Fallback to first symbol");
        return gameSymbols[0].symbol; // Fallback
    }, []);

    // Generate a random card with a symbol
    const generateCard = useCallback(() => {
        return {
            symbol: getRandomSymbol(),
            revealed: false
        };
    }, [getRandomSymbol]);

    // Generate a set of 12 cards with at least 3 matching symbols
    const generateCards = useCallback(() => {
        let cards;
        let hasMatches;
        
        do {
            cards = Array(12).fill(null).map(() => generateCard());
            
            // Count occurrences of each symbol
            const symbolCounts = {};
            for (let card of cards) {
                symbolCounts[card.symbol] = (symbolCounts[card.symbol] || 0) + 1;
            }
            
            // Check if there are at least 3 matching symbols
            hasMatches = Object.values(symbolCounts).some(count => count >= 3);
            
        } while (!hasMatches);
        
        // Add bonus features
        cards = addBonusFeatures(cards);
        
        return cards;
    }, [generateCard]);

    // Add bonus features to the cards
    const addBonusFeatures = useCallback((cards) => {
        // Deep copy the cards
        const newCards = JSON.parse(JSON.stringify(cards));
        
        // Determine if we should add a bonus feature
        const bonusRoll = Math.random();
        let bonusType = null;
        
        if (bonusRoll < bonusFeatures.chestGame.probability) {
            bonusType = 'Chest';
        }
        
        // If no bonus feature is selected, return the original cards
        if (!bonusType) return newCards;
        
        // Add 3 bonus symbols (always exactly 3 to ensure a match)
        const positions = [];
        while (positions.length < 3) {
            const pos = Math.floor(Math.random() * 12);
            if (!positions.includes(pos)) {
                positions.push(pos);
            }
        }
        
        // Add the bonus symbols
        for (let pos of positions) {
            newCards[pos].symbol = bonusType;
        }
        
        return newCards;
    }, []);

    // Helper function to calculate win for a specific match
    const calculateMatchWin = useCallback((symbol, matchCount, betAmount) => {
        // Get base payout for the symbol
        const baseMultiplier = gameSymbols.find(s => s.symbol === symbol).payout || 1;
        
        // Get match multiplier based on number of matches
        const matchMultiplier = matchMultipliers[matchCount] || 1;
        
        // Calculate total win
        return betAmount * baseMultiplier * matchMultiplier;
    }, []);

    // Find all matches in the current cards
    const findMatches = useCallback(() => {
        const matches = [];
        const symbols = new Set(gameState.cards.map(card => card.symbol));
        
        symbols.forEach(symbol => {
            const positions = gameState.cards
                .map((card, index) => ({ card, index }))
                .filter(({ card }) => card.symbol === symbol)
                .map(({ index }) => index);
                
            if (positions.length >= 3) {
                matches.push({ symbol, positions });
            }
        });
        
        return matches;
    }, [gameState.cards]);

    // Calculate win amount based on matches
    const calculateWinAmount = useCallback(() => {
        console.log("Calculating win amount...");
        
        // Find matches in the current cards
        const matches = findMatches();
        let totalWin = 0;
        
        // First check for regular symbol matches
        const regularMatches = matches.filter(match => !['Chest', 'Ticket'].includes(match.symbol));
        console.log("Regular matches:", regularMatches);
        
        // Process regular matches (if any)
        if (regularMatches.length > 0) {
            // Find the match with the highest potential payout
            let highestPayoutMatch = null;
            let highestPayout = 0;
            
            for (const match of regularMatches) {
                const win = calculateMatchWin(match.symbol, match.positions.length, gameState.betAmount);
                if (win > highestPayout) {
                    highestPayout = win;
                    highestPayoutMatch = match;
                }
            }
            
            // Only apply the highest payout
            if (highestPayoutMatch) {
                totalWin = highestPayout;
                console.log(`Setting win amount to ${totalWin} for highest match:`, highestPayoutMatch);
            }
        }
        
        return totalWin;
    }, [gameState.betAmount, findMatches, calculateMatchWin]);

    // Check for bonus features
    const checkForBonusFeatures = useCallback((cards, revealedCards) => {
        // Get all revealed cards
        const revealedSymbols = cards
            .filter((_, index) => revealedCards[index])
            .map(card => card.symbol);

        console.log("Revealed symbols:", revealedSymbols);

        // Check for chest symbols
        const chestSymbols = revealedSymbols.filter(symbol => symbol === 'Chest');
        if (chestSymbols.length >= 3) {
            return {
                activeBonus: 'chest',
                bonusData: {
                    chestCount: chestSymbols.length
                }
            };
        }

        // Check for lottery ticket symbols
        const ticketSymbols = revealedSymbols.filter(symbol => symbol === 'Ticket');
        if (ticketSymbols.length >= 1) {
            return {
                activeBonus: 'lottery',
                bonusData: {
                    ticketCount: ticketSymbols.length
                }
            };
        }

        return {
            activeBonus: null,
            bonusData: null
        };
    }, []);

    // Handle card reveal
    const handleCardReveal = useCallback((cardIndex) => {
        if (!gameState.cards[cardIndex] || gameState.revealedCards[cardIndex]) {
            return;
        }

        const newRevealedCards = [...gameState.revealedCards];
        newRevealedCards[cardIndex] = true;

        // Calculate current win amount
        const currentWinAmount = calculateWinAmount();
        
        // Check for bonus features
        const bonusFeatures = checkForBonusFeatures(gameState.cards, newRevealedCards);

        setGameState(prev => ({
            ...prev,
            revealedCards: newRevealedCards,
            winAmount: currentWinAmount,
            bonusFeatures: {
                ...prev.bonusFeatures,
                activeBonus: bonusFeatures.activeBonus,
                bonusData: bonusFeatures.bonusData
            }
        }));
    }, [gameState.cards, gameState.revealedCards, calculateWinAmount, checkForBonusFeatures]);

    // Reset the game
    const resetGame = useCallback(() => {
        console.log("Resetting game...");
        
        // Generate new cards
        const newCards = generateCards();
        
        // Reset game state
        setGameState(prev => ({
            ...prev,
            cards: newCards,
            revealedCards: Array(newCards.length).fill(false),
            isPlaying: true,
            matches: [],
            winAmount: 0,
            bonusFeatures: {
                ...prev.bonusFeatures,
                activeBonus: null,
                bonusData: null
            }
        }));
    }, [generateCards]);

    // Deduct bet amount from balance
    const deductBetAmount = useCallback((amount) => {
        // Make sure amount is a number
        const numericAmount = Number(amount);
        if (isNaN(numericAmount)) {
            console.error(`Invalid bet amount: ${amount} is not a number`);
            return false;
        }
        
        // Check if we have enough balance
        if (balance.jinu < numericAmount) {
            console.log(`Not enough balance: ${balance.jinu} < ${numericAmount}`);
            return false;
        }
        
        console.log(`%c DEDUCTING BET: ${numericAmount} JINU`, 'background: #ff5722; color: white; padding: 4px; font-size: 16px; font-weight: bold;');
        
        try {
            // CRITICAL FIX: Add the 'jinu' currency parameter to the subtractFromBalance function
            // This was the root cause of the issue - subtractFromBalance needs a currency parameter
            subtractFromBalance('jinu', numericAmount);
            
            // Update game stats
            setGameState(prev => ({
                ...prev,
                sessionStats: {
                    ...prev.sessionStats,
                    totalGames: prev.sessionStats.totalGames + 1,
                    totalWagered: prev.sessionStats.totalWagered + numericAmount,
                    netProfit: prev.sessionStats.netProfit - numericAmount
                },
                pools: {
                    ...prev.pools,
                    totalBets: prev.pools.totalBets + numericAmount,
                    prizePool: prev.pools.prizePool + numericAmount * poolAllocations.prizePool,
                    vipPool: prev.pools.vipPool + numericAmount * poolAllocations.vipPool,
                    lotteryPool: prev.pools.lotteryPool + numericAmount * poolAllocations.lotteryPool
                }
            }));
            
            // Add to rewards pool (10% of bet)
            addToRewardsPool && addToRewardsPool(numericAmount * 0.1);
            
            console.log(`%c BET SUCCESSFULLY DEDUCTED: ${numericAmount} JINU`, 'background: #ff5722; color: white; padding: 4px; font-size: 16px; font-weight: bold;');
            return true;
        } catch (error) {
            console.error('Error deducting bet amount from balance:', error);
            return false;
        }
    }, [balance, subtractFromBalance, addToRewardsPool]);

    // Add win amount to balance
    const addWinAmount = useCallback((amount) => {
        if (amount <= 0) {
            console.log('No win amount to add to balance');
            return;
        }
        
        // Round to 2 decimal places
        const exactAmount = Math.round(amount * 100) / 100;
        
        console.log(`%c ADDING WIN: ${exactAmount} JINU`, 'background: #00c853; color: white; padding: 4px; font-size: 16px; font-weight: bold;');
        
        try {
            // CRITICAL FIX: Add the 'jinu' currency parameter to the addToBalance function
            // This was the root cause of the issue - addToBalance needs a currency parameter
            addToBalance('jinu', exactAmount);
            
            // Update game stats
            setGameState(prev => {
                const newStats = {
                    ...prev.sessionStats,
                    totalWins: prev.sessionStats.totalWins + exactAmount,
                    biggestWin: Math.max(prev.sessionStats.biggestWin, exactAmount),
                    netProfit: prev.sessionStats.netProfit + exactAmount
                };
                
                return {
                    ...prev,
                    sessionStats: newStats
                };
            });
            
            // Log the updated balance after a short delay to allow state updates
            setTimeout(() => {
                console.log('Current balance after win:', balance);
            }, 100);
            
            console.log(`%c WIN SUCCESSFULLY ADDED TO BALANCE: ${exactAmount} JINU`, 'background: #00c853; color: white; padding: 4px; font-size: 16px; font-weight: bold;');
        } catch (error) {
            console.error('Error adding win amount to balance:', error);
        }
    }, [addToBalance, balance]);

    // Handle lottery ticket win
    const handleLotteryTicketWin = useCallback(() => {
        // Determine lottery ticket result
        // 60% chance of no prize, 30% chance of small win, 10% chance of free replay
        const rand = Math.random();
        let result, payout;
        
        if (rand < 0.6) {
            result = 'no_prize';
            payout = 0;
        } else if (rand < 0.9) {
            result = 'small_win';
            payout = 1.0; // Fixed payout of 1.0x
        } else {
            result = 'free_replay';
            payout = 1.0; // Fixed payout of 1.0x for tracking purposes
        }
        
        // Add to ticket history
        const ticketResult = {
            date: new Date(),
            result,
            payout
        };
        
        setGameState(prev => ({
            ...prev,
            ticketHistory: [...prev.ticketHistory, ticketResult]
        }));
        
        return ticketResult;
    }, []);

    // Handle bonus feature win
    const handleBonusFeatureWin = useCallback((bonusType, result = null, activateBonus = false) => {
        if (bonusType === 'chest') {
            // Handle chest bonus
            const chestMultiplier = result || Math.random() * (bonusFeatures.chestGame.maxMultiplier - bonusFeatures.chestGame.minMultiplier) + bonusFeatures.chestGame.minMultiplier;
            setGameState(prev => ({
                ...prev,
                bonusFeatures: {
                    ...prev.bonusFeatures,
                    chestBonus: true,
                    chestMultiplier,
                    activeBonus: activateBonus ? 'chest' : prev.bonusFeatures.activeBonus
                }
            }));
        }
    }, [bonusFeatures.chestGame.minMultiplier, bonusFeatures.chestGame.maxMultiplier]);

    // Start a new game
    const playGame = useCallback((isFreePlay = false) => {
        console.log("Playing game...");
        
        // Only deduct bet amount if it's not a free play
        if (!isFreePlay) {
            if (!deductBetAmount(gameState.betAmount)) {
                console.log("Not enough balance to play");
                return;
            }
        }
        
        // Generate new cards only if we're starting a new game
        if (!gameState.isPlaying) {
            resetGame();
        }
        
        // Rest of the playGame logic...
    }, [gameState.isPlaying, gameState.betAmount, deductBetAmount, resetGame]);

    // Check for pending free play
    useEffect(() => {
        if (gameState.pendingFreePlay && !gameState.isPlaying) {
            // Reset the pending flag and start a free game
            setGameState(prev => ({
                ...prev,
                pendingFreePlay: false
            }));
            
            // Small delay before starting free game
            setTimeout(() => {
                playGame(true);
            }, 1000);
        }
    }, [gameState.pendingFreePlay, gameState.isPlaying, playGame]);

    // Get the number of tickets collected
    const getTicketsCollected = useCallback(() => {
        return gameState.ticketsCollected;
    }, [gameState.ticketsCollected]);
    
    // Get the ticket history
    const getTicketHistory = useCallback(() => {
        return gameState.ticketHistory;
    }, [gameState.ticketHistory]);

    // Reset ticket count (for debugging)
    const resetTicketCount = useCallback(() => {
        setGameState(prev => ({
            ...prev,
            ticketsCollected: 0,
            ticketHistory: []
        }));
        console.log("Ticket count reset to 0");
    }, []);

    // Reveal all cards
    const revealAllCards = useCallback(() => {
        // Can't reveal if not playing
        if (!gameState.isPlaying) return;
        
        console.log("Revealing all cards");
        
        // Create a new array with all cards revealed
        const allRevealed = Array(12).fill(true);
        
        // Count ticket symbols that weren't already revealed
        const newTickets = gameState.cards
            .filter((card, index) => card.symbol === 'Ticket' && !gameState.revealedCards[index])
            .length;
        
        console.log(`Revealing all cards, found ${newTickets} new tickets`);
        
        // Update tickets collected - tickets don't pay tokens, they give lottery entries
        if (newTickets > 0) {
            setGameState(prev => ({
                ...prev,
                ticketsCollected: prev.ticketsCollected + newTickets
            }));
        }
        
        // Calculate win amount
        const winAmount = calculateWinAmount();
        
        // Find matches
        const matches = findMatches();
        
        // Check for bonus features
        const bonusFeaturesResult = checkForBonusFeatures(gameState.cards, allRevealed);
        
        // Update game state
        setGameState(prev => ({
            ...prev,
            revealedCards: allRevealed,
            isPlaying: false, // Game is done but not reset
            matches,
            winAmount,
            bonusFeatures: {
                ...prev.bonusFeatures,
                activeBonus: bonusFeaturesResult.activeBonus,
                bonusData: bonusFeaturesResult.bonusData
            }
        }));
        
        // Add win amount to balance
        if (winAmount > 0) {
            addWinAmount(winAmount);
        }
        
        // Handle bonus features if any
        if (bonusFeaturesResult.activeBonus) {
            handleBonusFeatureWin(bonusFeaturesResult.activeBonus, null);
        }
        
        // No auto-reset anymore
    }, [gameState.isPlaying, gameState.cards, gameState.revealedCards, calculateWinAmount, findMatches, checkForBonusFeatures, addWinAmount, handleBonusFeatureWin]);

    // Flip a card
    const flipCard = useCallback((index) => {
        // Can't flip if not playing or already revealing
        if (!gameState.isPlaying || gameState.isRevealing) return;
        
        // Can't flip an already revealed card
        if (gameState.revealedCards[index]) return;
        
        console.log(`Flipping card at index ${index}, symbol: ${gameState.cards[index].symbol}`);
        
        // Create a new array with the updated revealed card
        const newRevealedCards = [...gameState.revealedCards];
        newRevealedCards[index] = true;

        // Check if the revealed card is a ticket
        const isTicket = gameState.cards[index].symbol === 'Ticket';
        
        // Update the game state with the new revealed card
        setGameState(prev => ({
            ...prev,
            revealedCards: newRevealedCards,
            isRevealing: true,
            // Increment ticket count if the card is a ticket - tickets don't pay tokens, they give lottery entries
            ticketsCollected: isTicket ? prev.ticketsCollected + 1 : prev.ticketsCollected
        }));

        // Check for matches and bonus features after a short delay
        setTimeout(() => {
            // Find matches with the current revealed cards
            const currentMatches = findMatches();
            console.log("Current matches after flip:", currentMatches);
            
            // Calculate current win amount
            const currentWinAmount = calculateWinAmount();
            console.log("Current win amount after flip:", currentWinAmount);
            
            // Check for bonus features
            const bonusFeature = checkForBonusFeatures(gameState.cards, newRevealedCards);
            console.log("Bonus feature after flip:", bonusFeature);
            
            // Count how many of each symbol are revealed
            const revealedSymbolCounts = {};
            for (let i = 0; i < gameState.cards.length; i++) {
                if (newRevealedCards[i]) {
                    const symbol = gameState.cards[i].symbol;
                    revealedSymbolCounts[symbol] = (revealedSymbolCounts[symbol] || 0) + 1;
                }
            }
            console.log("Revealed symbol counts:", revealedSymbolCounts);
            
            setGameState(prev => ({
                ...prev,
                isRevealing: false,
                matches: currentMatches,
                winAmount: currentWinAmount,
                bonusFeatures: {
                    ...prev.bonusFeatures,
                    activeBonus: bonusFeature.activeBonus,
                    bonusData: bonusFeature.bonusData
                }
            }));
            
            // Add win amount to balance if game is complete
            if (newRevealedCards.every(revealed => revealed) && currentWinAmount > 0) {
                console.log("Game complete, adding win amount to balance:", currentWinAmount);
                addWinAmount(currentWinAmount);
            }
            
            // Handle bonus features if any
            if (bonusFeature.activeBonus) {
                handleBonusFeatureWin(bonusFeature.activeBonus, null);
            }
            
            // Check if all cards are now revealed
            const allRevealed = newRevealedCards.every(card => card);
            if (allRevealed) {
                console.log("All cards revealed through flipping");
                
                // Set game state to not playing but don't reset
                setGameState(prev => ({
                    ...prev,
                    isPlaying: false,
                    isRevealing: false
                }));
                
                // No auto-reset anymore
            } else {
                // Not all cards are revealed yet, continue playing
                setGameState(prev => ({
                    ...prev,
                    isRevealing: false
                }));
            }
        }, 500);
    }, [gameState.cards, gameState.revealedCards, gameState.isRevealing, gameState.isPlaying, findMatches, calculateWinAmount, checkForBonusFeatures, addWinAmount, handleBonusFeatureWin]);

    // Change bet amount
    const changeBetAmount = useCallback((amount) => {
        if (amount < 1) amount = 1;
        if (amount > balance) amount = balance;
        
        setGameState(prev => ({
            ...prev,
            betAmount: amount
        }));
    }, [balance]);

    // Toggle auto play
    const toggleAutoPlay = useCallback((options = {}) => {
        const { speed, count, forceState } = options;
        
        setGameState(prev => {
            // Determine if we should toggle or force a specific state
            const newAutoPlayState = forceState !== undefined ? forceState : !prev.autoPlay;
            
            return {
                ...prev,
                autoPlay: newAutoPlayState,
                autoPlaySpeed: speed !== undefined ? speed : prev.autoPlaySpeed,
                autoPlayCount: count !== undefined ? count : prev.autoPlayCount || 10,
                autoPlayRemaining: count !== undefined ? count : prev.autoPlayRemaining || 0
            };
        });
    }, []);

    // Auto play effect
    useEffect(() => {
        let autoPlayInterval;
        
        if (gameState.autoPlay && !gameState.isPlaying && balance >= gameState.betAmount) {
            // Check if we've reached the auto play count limit
            if (gameState.autoPlayRemaining <= 0 && gameState.autoPlayCount > 0) {
                // Initialize the remaining count when starting a new auto play session
                setGameState(prev => ({
                    ...prev,
                    autoPlayRemaining: prev.autoPlayCount
                }));
            } else if (gameState.autoPlayRemaining <= 0) {
                // Stop auto play if we've reached the limit
                setGameState(prev => ({
                    ...prev,
                    autoPlay: false
                }));
                return;
            }
            
            // First, play the game
            autoPlayInterval = setTimeout(() => {
                playGame();
                
                // Then, after a delay, reveal all cards
                const revealTimeout = setTimeout(() => {
                    // We need to make sure we're still in playing state by checking the ref
                    if (gameStateRef.current.isPlaying) {
                        revealAllCards();
                        
                        // Decrement the remaining count
                        setGameState(prev => ({
                            ...prev,
                            autoPlayRemaining: Math.max(0, prev.autoPlayRemaining - 1)
                        }));
                    }
                }, gameState.autoPlaySpeed || 500); // Use the user's preferred speed or default to 500ms
                
                return () => {
                    clearTimeout(revealTimeout);
                };
            }, gameState.autoPlaySpeed ? gameState.autoPlaySpeed / 2 : 750); // Half the reveal time for the play action
        }
        
        return () => {
            if (autoPlayInterval) clearTimeout(autoPlayInterval);
        };
    }, [gameState.autoPlay, gameState.isPlaying, balance, gameState.betAmount, gameState.autoPlaySpeed, gameState.autoPlayCount, gameState.autoPlayRemaining, playGame, revealAllCards]);

    // Calculate return rate
    const calculateReturnRate = useCallback(() => {
        if (gameState.sessionStats.totalWagered === 0) return 0;
        return (gameState.sessionStats.totalWins / gameState.sessionStats.totalWagered) * 100;
    }, [gameState.sessionStats]);

    // Reset session stats
    const resetSessionStats = useCallback(() => {
        setGameState(prev => ({
            ...prev,
            sessionStats: {
                totalGames: 0,
                totalWins: 0,
                totalWagered: 0,
                biggestWin: 0,
                netProfit: 0
            }
        }));
    }, []);

    // Add funds to balance
    const addFunds = useCallback((amount) => {
        addToBalance(amount);
    }, [addToBalance]);

    // Reset bonus features
    const resetBonusFeatures = useCallback(() => {
        setGameState(prev => ({
            ...prev,
            bonusFeatures: {
                wheelBonus: false,
                wheelMultiplier: 1,
                chestBonus: false,
                chestMultiplier: 1,
                lotteryBonus: false,
                lotteryResult: null,
                activeBonus: null,
                bonusData: null
            }
        }));
    }, []);

    // Get balance
    const getBalance = useCallback(() => {
        // Return the JINU balance specifically, not the entire balance object
        return balance?.jinu || 0;
    }, [balance]);
    
    // Get session stats
    const getSessionStats = useCallback(() => {
        return gameState.sessionStats;
    }, [gameState.sessionStats]);

    // Create a function to set bonus features
    const setBonusFeatures = useCallback((bonusFeatures) => {
        setGameState(prev => ({
            ...prev,
            bonusFeatures: typeof bonusFeatures === 'function' 
                ? bonusFeatures(prev.bonusFeatures) 
                : bonusFeatures
        }));
    }, []);

    // Allocate tickets to the wheel of fortune
    const allocateTicketsToWheel = useCallback((amount) => {
        if (amount <= 0) return;
        
        // Check if we have enough available tickets
        const availableTickets = gameState.ticketsCollected - gameState.wheelTickets;
        if (amount > availableTickets) {
            console.log(`Not enough available tickets. Requested: ${amount}, Available: ${availableTickets}`);
            return;
        }
        
        // Update the wheel tickets count
        setGameState(prev => ({
            ...prev,
            wheelTickets: prev.wheelTickets + amount
        }));
        
        console.log(`Allocated ${amount} tickets to the Wheel of Fortune`);
    }, [gameState.ticketsCollected, gameState.wheelTickets]);
    
    // Convert tickets to JINU
    const convertTicketsToJinu = useCallback((amount) => {
        if (amount <= 0 || amount % 10 !== 0) {
            console.log(`Invalid conversion amount: ${amount}. Must be a positive multiple of 10.`);
            return;
        }
        
        // Check if we have enough available tickets
        const availableTickets = gameState.ticketsCollected - gameState.wheelTickets;
        if (amount > availableTickets) {
            console.log(`Not enough available tickets. Requested: ${amount}, Available: ${availableTickets}`);
            return;
        }
        
        // Calculate JINU amount (10 tickets = 1 JINU)
        const jinuAmount = amount / 10;
        
        // Update tickets count
        setGameState(prev => ({
            ...prev,
            ticketsCollected: prev.ticketsCollected - amount
        }));
        
        // TODO: Add logic to credit JINU to user's wallet
        console.log(`Converted ${amount} tickets to ${jinuAmount} JINU`);
        
        // Return the JINU amount for further processing if needed
        return jinuAmount;
    }, [gameState.ticketsCollected, gameState.wheelTickets]);

    // Create a global helper function for updating the balance
    if (typeof window !== 'undefined') {
        window.forceUpdateJinuBalance = (amount) => {
            console.log(`%c GLOBAL: Force updating JINU balance by ${amount}`, 'background: #e91e63; color: white; padding: 4px; font-size: 16px; font-weight: bold;');
            addToBalance('jinu', amount);
        };
    }

    return (
        <GameContext.Provider value={{
            // Game state
            gameState,
            
            // Game actions
            playGame,
            resetGame,
            revealCard: handleCardReveal,
            revealAllCards,
            flipCard,
            changeBetAmount,
            toggleAutoPlay,
            
            // Bonus feature handlers
            handleBonusFeatureWin,
            
            // Utility functions
            getGameSymbols,
            getMatchMultipliers,
            getTicketsCollected,
            
            // Ticket management
            allocateTicketsToWheel,
            convertTicketsToJinu,
            
            // Balance management
            balance,
            updateBalance: (currency, amount) => {
                console.log(`%c SCRATCH CARD: Updating ${currency} balance by ${amount}`, 'background: #9c27b0; color: white; padding: 4px; font-size: 16px; font-weight: bold;');
                // CRITICAL FIX: Pass both currency and amount parameters to addToBalance
                addToBalance(currency, amount);
                
                // Also log the current balance after update
                setTimeout(() => {
                    console.log('Current balance after scratch card win:', balance);
                }, 100);
            },
            
            // State setters
            setBonusFeatures,
            setGameState
        }}>
            {children}
        </GameContext.Provider>
    );
}
